import {
    ConsolidationEligibilityRule,
    ConsolidationEligibilityViolation,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { joinWithAnd } from "@helpers/joinWithAnd";
import { isActiveRelationship } from "../ownershipGraph.helpers";

const OUTSIDE_OWNER_MESSAGE =
    "The member is owned by an entity other than the parent";
const DEFAULT_ELIGIBILITY_ERROR =
    "Consolidation blocked. These entities are not eligible for consolidation.";

interface NamedEntity {
    uuid: string;
    name: string | null;
}

interface ConsolidationEligibilityErrorMessageInput {
    violations: ConsolidationEligibilityViolation[];
    relationships: ConsolidationRelationshipResponse[];
    entities: NamedEntity[];
    parentEntityUuid: string;
}

function getNonBlankName(
    name: string | null | undefined,
    fallback: string,
): string {
    const trimmed = name?.trim();
    return (trimmed === "" ? undefined : trimmed) ?? fallback;
}

export function getConsolidationEligibilityErrorMessage({
    violations,
    relationships,
    entities,
    parentEntityUuid,
}: ConsolidationEligibilityErrorMessageInput): string {
    const namesByEntityUuid = new Map(
        entities.map((entity) => [entity.uuid, entity.name]),
    );
    const conflictingOwnerUuidsByMemberUuid = new Map<string, Set<string>>();
    const otherMessages = new Set<string>();

    for (const violation of violations) {
        if (
            violation.rule !== ConsolidationEligibilityRule.notFlatHierarchy ||
            violation.entityUuid === null ||
            violation.message !== OUTSIDE_OWNER_MESSAGE
        ) {
            otherMessages.add(violation.message);
            continue;
        }
        const memberUuid = violation.entityUuid;

        const conflictingOwnerUuids = relationships
            .filter(
                (relationship) =>
                    isActiveRelationship(relationship) &&
                    relationship.type ===
                        ConsolidationEntityRelationshipType.ownership &&
                    relationship.targetEntityUuid === memberUuid &&
                    relationship.sourceEntityUuid !== parentEntityUuid,
            )
            .map((relationship) => relationship.sourceEntityUuid);
        if (conflictingOwnerUuids.length === 0) {
            otherMessages.add(violation.message);
            continue;
        }

        const ownerUuids =
            conflictingOwnerUuidsByMemberUuid.get(memberUuid) ??
            new Set<string>();
        for (const ownerUuid of conflictingOwnerUuids) {
            ownerUuids.add(ownerUuid);
        }
        conflictingOwnerUuidsByMemberUuid.set(memberUuid, ownerUuids);
    }

    const conflictDetails = Array.from(
        conflictingOwnerUuidsByMemberUuid.entries(),
    ).map(([memberUuid, ownerUuids]) => {
        const memberName = getNonBlankName(
            namesByEntityUuid.get(memberUuid),
            "A selected subsidiary",
        );
        const ownerNames = Array.from(
            new Set(
                Array.from(ownerUuids).map((ownerUuid) =>
                    getNonBlankName(
                        namesByEntityUuid.get(ownerUuid),
                        "another entity",
                    ),
                ),
            ),
        );
        return `${memberName} is also owned by ${joinWithAnd(ownerNames)}.`;
    });

    if (conflictDetails.length > 0) {
        const otherDetails =
            otherMessages.size > 0
                ? ` ${Array.from(otherMessages).join(" • ")}`
                : "";
        return `Consolidation blocked. ${conflictDetails.join(" ")} V1 consolidation supports subsidiaries owned 100% by a single parent.${otherDetails}`;
    }

    if (otherMessages.size > 0) {
        return `Consolidation blocked. ${Array.from(otherMessages).join(" • ")}`;
    }

    return DEFAULT_ELIGIBILITY_ERROR;
}
